import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { chatService } from '../services/chatService';


const Chat = ({ caseId, onNewMessage, messages: externalMessages = [] }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = async () => {
    try {
      setIsLoading(true);
      const data = await chatService.getCaseMessages(caseId);
      setMessages(data);
      setError(null);
    } catch (err) {
      console.error('Error loading chat messages:', err);
      setError(t('chat.errorLoading'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      const messageData = {
        case_id: caseId,
        message: newMessage.trim()
      };

      const sentMessage = await chatService.createMessage(messageData);
      
      // Add message to local state
      setMessages(prev => [...prev, sentMessage]);
      setNewMessage('');
      
      // Notify parent component if callback provided
      if (onNewMessage) {
        onNewMessage(sentMessage);
      }

      scrollToBottom();
    } catch (err) {
      console.error('Error sending message:', err);
      setError(t('chat.errorSending'));
    }
  };

  const handleMessageReceived = (messageData) => {
    // Add received message to local state if not already present
    setMessages(prev => {
      const exists = prev.some(msg => msg.id === messageData.id);
      if (exists) return prev;
      return [...prev, messageData];
    });
    scrollToBottom();
  };

  const formatMessageTime = (timestamp) => {
    return formatDistanceToNow(new Date(timestamp), { 
      addSuffix: true
    });
  };

  const isOwnMessage = (message) => {
    return message.user_id === user?.id;
  };

  useEffect(() => {
    if (caseId) {
      loadMessages();
    }
  }, [caseId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle external messages (from WebSocket)
  useEffect(() => {
    if (externalMessages.length > 0) {
      const latestMessage = externalMessages[externalMessages.length - 1];
      handleMessageReceived(latestMessage);
    }
  }, [externalMessages]);

  // Listen for WebSocket chat messages
  useEffect(() => {
    const handleChatMessage = (event) => {
      const messageData = event.detail;
      handleMessageReceived(messageData);
      
      // Notify parent of new message if provided
      if (onNewMessage) {
        onNewMessage(messageData);
      }
    };

    window.addEventListener('collaboration-chat-message', handleChatMessage);
    
    return () => {
      window.removeEventListener('collaboration-chat-message', handleChatMessage);
    };
  }, [onNewMessage]);

  if (isLoading && messages.length === 0) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="text-sm text-neutral-500">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Messages Container */}
      <div 
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-3 space-y-2 min-h-0"
      >
        {error && (
          <div className="text-sm text-red-600 bg-red-50 p-2 rounded-lg">
            {error}
          </div>
        )}

        {messages.length === 0 && !isLoading ? (
          <div className="text-center text-sm text-neutral-500 py-8">
            {t('chat.noMessages')}
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${isOwnMessage(message) ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-60 px-3 py-2 rounded-lg ${
                isOwnMessage(message)
                  ? 'bg-accent-600 text-white'
                  : 'bg-neutral-100 text-neutral-900'
              }`}>
                {!isOwnMessage(message) && (
                  <div className="text-xs font-medium mb-1 opacity-75">
                    {message.user_full_name || message.user_username}
                  </div>
                )}
                <div className="text-sm">{message.message}</div>
                <div className={`text-xs mt-1 ${
                  isOwnMessage(message) ? 'text-accent-100' : 'text-neutral-500'
                }`}>
                  {formatMessageTime(message.created_at)}
                  {message.edited_at && (
                    <span className="ml-1">• {t('chat.edited')}</span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <form onSubmit={handleSendMessage} className="p-3 border-t border-neutral-200">
        <div className="flex space-x-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={t('chat.typePlaceholder')}
            className="flex-1 px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent"
            disabled={isLoading}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-accent-600 rounded-lg hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('chat.send')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Chat;